import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import * as React from 'react';
import { cn } from '../../lib/utils';
import { Badge } from '../atoms/badge';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface KanbanCard {
  id: string;
  [key: string]: unknown;
}

export interface KanbanColumn {
  id: string;
  title: string;
  /** Count shown in badge next to title. If undefined, uses cards.length */
  count?: number;
  cards: KanbanCard[];
  /** Colors the column header accent */
  status?: 'default' | 'warning' | 'success' | 'destructive';
}

export interface KanbanBoardProps {
  columns: KanbanColumn[];
  /** Render prop for each card — consumer provides the card UI */
  renderCard: (card: KanbanCard, columnId: string) => React.ReactNode;
  /** Called when a card is dropped into a new column (optimistic update — consumer handles state) */
  onCardMove?: (cardId: string, fromColumnId: string, toColumnId: string) => void;
  /** Optional empty state per column */
  emptyState?: React.ReactNode;
  className?: string;
}

// ─── Status → Badge variant map ───────────────────────────────────────────────

const statusVariantMap = {
  default: 'secondary',
  warning: 'warning',
  success: 'success',
  destructive: 'destructive',
} as const satisfies Record<
  NonNullable<KanbanColumn['status']>,
  'secondary' | 'warning' | 'success' | 'destructive'
>;

// ─── DraggableCard ────────────────────────────────────────────────────────────

interface DraggableCardProps {
  card: KanbanCard;
  columnId: string;
  renderCard: (card: KanbanCard, columnId: string) => React.ReactNode;
}

function DraggableCard({ card, columnId, renderCard }: DraggableCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
    data: { columnId },
  });

  return (
    <li>
      <div
        ref={setNodeRef}
        aria-grabbed={isDragging}
        className={cn('touch-none', isDragging && 'opacity-40')}
        {...listeners}
        {...attributes}
      >
        {renderCard(card, columnId)}
      </div>
    </li>
  );
}

// ─── DroppableColumn ──────────────────────────────────────────────────────────

interface DroppableColumnProps {
  column: KanbanColumn;
  renderCard: (card: KanbanCard, columnId: string) => React.ReactNode;
  emptyState?: React.ReactNode;
}

function DroppableColumn({ column, renderCard, emptyState }: DroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  const count = column.count ?? column.cards.length;
  const badgeVariant = statusVariantMap[column.status ?? 'default'];

  return (
    <section
      aria-label={column.title}
      className="flex min-w-[280px] w-[280px] flex-col rounded-lg bg-muted/40 border border-border"
    >
      {/* Column header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <span className="text-sm font-medium text-foreground">{column.title}</span>
        <Badge appearance="soft" variant={badgeVariant} size="sm">
          {count}
        </Badge>
      </div>

      {/* Card list */}
      <ul
        ref={setNodeRef}
        className={cn(
          'flex flex-col gap-2 p-2 flex-1 overflow-y-auto min-h-[200px] max-h-[calc(100vh-200px)] transition-colors list-none',
          isOver && 'bg-muted/60',
        )}
      >
        {column.cards.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-6">
            {emptyState ?? <span className="text-sm text-muted-foreground">No items</span>}
          </div>
        ) : (
          column.cards.map((card) => (
            <DraggableCard key={card.id} card={card} columnId={column.id} renderCard={renderCard} />
          ))
        )}
      </ul>
    </section>
  );
}

// ─── KanbanBoard ──────────────────────────────────────────────────────────────

const KanbanBoard = React.forwardRef<HTMLDivElement, KanbanBoardProps>(
  ({ columns, renderCard, onCardMove, emptyState, className, ...props }, ref) => {
    const [activeCard, setActiveCard] = React.useState<{
      card: KanbanCard;
      columnId: string;
    } | null>(null);

    const cardColumnMap = React.useMemo(() => {
      const map = new Map<string, string>();
      for (const col of columns) {
        for (const card of col.cards) {
          map.set(card.id, col.id);
        }
      }
      return map;
    }, [columns]);

    const cardMap = React.useMemo(() => {
      const map = new Map<string, KanbanCard>();
      for (const col of columns) {
        for (const card of col.cards) {
          map.set(card.id, card);
        }
      }
      return map;
    }, [columns]);

    function handleDragStart(event: DragStartEvent) {
      const cardId = String(event.active.id);
      const card = cardMap.get(cardId);
      const columnId = cardColumnMap.get(cardId);
      if (card && columnId) {
        setActiveCard({ card, columnId });
      }
    }

    function handleDragEnd(event: DragEndEvent) {
      const { active, over } = event;
      setActiveCard(null);

      if (!over) return;

      const cardId = String(active.id);
      const fromColumnId = cardColumnMap.get(cardId);
      const toColumnId = String(over.id);

      if (fromColumnId && toColumnId && fromColumnId !== toColumnId) {
        onCardMove?.(cardId, fromColumnId, toColumnId);
      }
    }

    function handleDragCancel() {
      setActiveCard(null);
    }

    return (
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div ref={ref} className={cn('flex gap-4 overflow-x-auto pb-4', className)} {...props}>
          {columns.map((column) => (
            <DroppableColumn
              key={column.id}
              column={column}
              renderCard={renderCard}
              emptyState={emptyState}
            />
          ))}
        </div>

        <DragOverlay>
          {activeCard ? (
            <div className="cursor-grabbing opacity-90 shadow-lg">
              {renderCard(activeCard.card, activeCard.columnId)}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  },
);

KanbanBoard.displayName = 'KanbanBoard';

export { KanbanBoard };
